/* StooretPulse scripts for the homepage sections and the search panel: hero heading, product row, shelf, routine steps,
   FAQ, count-up numbers and back to top. Progressive enhancement only. */
(() => {
  'use strict';

  const theme = window.theme || { routes: {}, strings: {} };
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  /* ---------- Hero heading split ---------- */

  const splitHeading = (heading) => {
    if (heading.dataset.split === 'done') return;
    heading.dataset.split = 'done';
    const label = heading.textContent.replace(/\s+/g, ' ').trim();
    let word = 0;
    let letterDelay = 0.42;
    const wrapWords = (node, inEm) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.append(' '); return; }
            const outer = document.createElement('span');
            outer.className = 'split-word';
            outer.setAttribute('aria-hidden', 'true');
            const inner = document.createElement('span');
            if (inEm) {
              [...part].forEach((ch) => {
                const l = document.createElement('span');
                l.className = 'split-letter';
                l.textContent = ch;
                l.style.setProperty('--d', `${letterDelay.toFixed(3)}s`);
                letterDelay += 0.045;
                inner.append(l);
              });
              inner.style.animation = 'none';
            } else {
              inner.textContent = part;
              inner.style.setProperty('--d', `${(0.12 + word * 0.1 + (word > 1 ? 0.3 : 0)).toFixed(2)}s`);
              word += 1;
            }
            outer.append(inner);
            frag.append(outer);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          wrapWords(child, inEm || child.tagName === 'EM');
        }
      });
    };
    wrapWords(heading, false);
    const sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = label;
    heading.prepend(sr);
  };

  if (document.body.classList.contains('has-animations') && !reducedMotion.matches) {
    document.querySelectorAll('.hero__slide.is-first [data-split-heading]').forEach(splitHeading);
  }

  /* ---------- Horizontal scroll row with arrows ---------- */

  class ScrollRow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroll-track]');
      this.prev = this.querySelector('[data-scroll-prev]');
      this.next = this.querySelector('[data-scroll-next]');
      this.arrows = this.prev?.parentElement;
      if (!this.track) return;
      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', debounce(this.update, 60), { passive: true });
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.track);
      this.update();
    }
    disconnectedCallback() { this.observer?.disconnect(); }
    step(dir) {
      const item = this.track.querySelector('[data-slide]');
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const amount = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: dir * amount, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (this.arrows) this.arrows.hidden = max < 4;
      if (this.prev) this.prev.disabled = this.track.scrollLeft < 4;
      if (this.next) this.next.disabled = this.track.scrollLeft > max - 4;
    }
  }
  customElements.define('scroll-row', ScrollRow);

  /* ---------- Shelf: filter pills ---------- */

  class ShelfFilter extends HTMLElement {
    connectedCallback() {
      this.pills = [...this.querySelectorAll('[data-filter]')];
      this.items = [...this.querySelectorAll('.shelf__item')];
      this.status = this.querySelector('[data-filter-status]');
      this.pills.forEach((pill) => pill.addEventListener('click', () => this.apply(pill)));
    }
    apply(pill) {
      const value = pill.dataset.filter;
      this.pills.forEach((p) => p.setAttribute('aria-pressed', String(p === pill)));
      let shown = 0;
      this.items.forEach((item) => {
        const match = !value || item.dataset.type === value;
        item.hidden = !match;
        item.classList.remove('is-entering');
        if (match) {
          shown += 1;
          if (!reducedMotion.matches) { void item.offsetWidth; item.classList.add('is-entering'); }
        }
      });
      if (this.status) this.status.textContent = (theme.strings.filterCount || '[count]').replace('[count]', shown);
    }
  }
  customElements.define('shelf-filter', ShelfFilter);

  /* ---------- Shelf card: size choice updates price and button ---------- */

  document.addEventListener('change', (e) => {
    const form = e.target.closest('.shelf-card__form');
    if (!form || e.target.name !== 'id') return;
    const source = e.target.tagName === 'SELECT' ? e.target.selectedOptions[0] : e.target;
    if (!source) return;
    const card = form.closest('.shelf-card');
    const { price, compare = '', save = '' } = source.dataset;
    const available = source.dataset.available === 'true';
    form.querySelector('.shelf-card__amount').textContent = price;
    const compareEl = form.querySelector('.shelf-card__compare');
    if (compareEl) { compareEl.textContent = compare; compareEl.hidden = !compare; }
    const saveEl = card?.querySelector('.shelf-card__save');
    if (saveEl) { saveEl.textContent = save; saveEl.hidden = !save; }
    const button = form.querySelector('.shelf-card__add');
    if (button) {
      button.disabled = !available;
      button.toggleAttribute('data-unavailable', !available);
      if (available) {
        button.textContent = `${button.dataset.label} `;
        const dash = document.createElement('span');
        dash.setAttribute('aria-hidden', 'true');
        dash.textContent = '—';
        const amount = document.createElement('span');
        amount.dataset.buttonPrice = '';
        amount.textContent = price;
        button.append(dash, ' ', amount);
      } else {
        button.textContent = button.dataset.soldOut;
      }
    }
  });

  /* ---------- Routine steps: one open step, matching picture ---------- */

  class RitualSteps extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll('.ritual__step')];
      this.frames = [...this.querySelectorAll('[data-frame]')];
      this.steps.forEach((step, i) => {
        step.querySelector('.ritual__toggle')?.addEventListener('click', () => this.open(i));
      });
    }
    open(i) {
      const already = this.steps[i]?.classList.contains('is-open');
      this.steps.forEach((step, n) => {
        const on = n === i && !already;
        step.classList.toggle('is-open', on);
        step.querySelector('.ritual__toggle')?.setAttribute('aria-expanded', String(on));
      });
      if (already) return;
      this.frames.forEach((frame, n) => {
        frame.classList.toggle('is-active', n === i);
        frame.setAttribute('aria-hidden', String(n !== i));
      });
    }
  }
  customElements.define('ritual-steps', RitualSteps);

  /* ---------- FAQ: smooth open and close ---------- */

  class FaqList extends HTMLElement {
    connectedCallback() {
      if (reducedMotion.matches || !Element.prototype.animate) return;
      this.querySelectorAll('details').forEach((details) => {
        const summary = details.querySelector('summary');
        const answer = details.querySelector('.faq__answer');
        if (!summary || !answer) return;
        summary.addEventListener('click', (e) => {
          e.preventDefault();
          if (details.open) {
            this.collapse(details, answer);
          } else {
            this.querySelectorAll('details[open]').forEach((d) => { if (d !== details) this.collapse(d, d.querySelector('.faq__answer')); });
            details.open = true;
            answer.classList.add('is-animating');
            answer.animate([{ height: '0px', opacity: 0 }, { height: `${answer.scrollHeight}px`, opacity: 1 }], { duration: 320, easing: 'cubic-bezier(.2,.7,.2,1)' })
              .finished.finally(() => answer.classList.remove('is-animating'));
          }
        });
      });
    }
    collapse(details, answer) {
      if (!answer) { details.open = false; return; }
      answer.classList.add('is-animating');
      answer.animate([{ height: `${answer.scrollHeight}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 260, easing: 'ease-in' })
        .finished.then(() => { details.open = false; }).finally(() => answer.classList.remove('is-animating'));
    }
  }
  customElements.define('faq-list', FaqList);

  /* ---------- Count-up numbers ---------- */

  const countUp = (el) => {
    const raw = el.textContent.trim();
    const match = raw.match(/^(\D*)(\d+(?:[.,]\d+)?)(.*)$/);
    if (!match) return;
    const [, prefix, num, suffix] = match;
    const target = parseFloat(num.replace(',', '.'));
    const decimals = (num.split(/[.,]/)[1] || '').length;
    const sep = num.includes(',') ? ',' : '.';
    const start = performance.now();
    const duration = 1400;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      el.textContent = `${prefix}${(target * eased).toFixed(decimals).replace('.', sep)}${suffix}`;
      if (p < 1) requestAnimationFrame(tick);
      else el.textContent = raw;
    };
    requestAnimationFrame(tick);
  };
  if (!reducedMotion.matches && 'IntersectionObserver' in window) {
    const counters = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { if (entry.isIntersecting) { counters.unobserve(entry.target); countUp(entry.target); } });
    }, { threshold: 0.6 });
    document.querySelectorAll('[data-count-up]').forEach((el) => counters.observe(el));
  }

  /* ---------- Back to top ---------- */

  class BackToTop extends HTMLElement {
    connectedCallback() {
      this.hidden = false;
      this.button = this.querySelector('a');
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.button?.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        const main = document.getElementById('MainContent');
        if (main) { main.setAttribute('tabindex', '-1'); main.focus({ preventScroll: true }); }
      });
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    onScroll() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
        this.style.setProperty('--progress', progress.toFixed(4));
        const visible = window.scrollY > window.innerHeight * 0.6;
        this.classList.toggle('is-visible', visible);
        if (this.button) this.button.tabIndex = visible ? 0 : -1;
      });
    }
  }
  customElements.define('back-to-top', BackToTop);

  /* ---------- Search panel: popular terms and featured list ---------- */

  document.querySelectorAll('.search-panel').forEach((panel) => {
    const input = panel.querySelector('input[type="search"]');
    const label = panel.querySelector('[data-results-label]');
    if (!input) return;
    const sync = () => {
      const hasQuery = input.value.trim() !== '';
      panel.classList.toggle('has-query', hasQuery);
      if (label) label.textContent = hasQuery ? label.dataset.labelResults : (panel.querySelector('[data-featured]') ? label.dataset.labelFeatured : '');
    };
    input.addEventListener('input', sync);
    panel.querySelectorAll('[data-search-term]').forEach((term) => {
      term.addEventListener('click', (e) => {
        e.preventDefault();
        input.value = term.dataset.searchTerm;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      });
    });
    sync();
  });

  // Theme editor: open the routine step that is selected in the sidebar.
  document.addEventListener('shopify:block:select', (e) => {
    const ritual = e.target.closest('ritual-steps');
    if (ritual && ritual.steps) {
      const i = ritual.steps.indexOf(e.target);
      if (i > -1 && !ritual.steps[i].classList.contains('is-open')) ritual.open(i);
    }
  });
})();
